import React, { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import {
  TextInput,
  RadioButtonGroup,
  RadioButton,
  Checkbox,
  DatePicker,
  DatePickerInput,
  MultiSelect,
  InlineNotification,
  Select,
  SelectItem,
  TextArea
} from 'carbon-components-react';
import {
  BelEnquiryDetails,
  BelValidationSummary
} from 'redux/bulkEnquiryLodgement/types';
import {
  getFileAttributeFieldsForDropdown,
  pickDefaultUserReferenceProperty
} from '../userReferenceProperty.utils';
import { addAlert, removeAlertByGroup } from 'redux/alerts/actions';
import Utils from 'utilities/helper.util';
import dayjs from 'dayjs';

interface DetailsStepProps {
  details: BelEnquiryDetails;
  validationSummary: BelValidationSummary | null;
  onChange: (details: BelEnquiryDetails) => void;
}

const LOCATION_TYPE_OPTIONS = [
  'Footpath',
  'Nature strip',
  'Road',
  'Private property'
];

const DESIGN_ACTIVITIES = [
  { id: 'CONVEYANCING', label: 'Conveyancing' },
  { id: 'PLANNING_DESIGN', label: 'Planning & Design' },
  { id: 'SUBDIVISION', label: 'Subdivision' },
  { id: 'TENDERING', label: 'Tendering' }
];

const EXCAVATION_ACTIVITIES = [
  { id: 'BLASTING', label: 'Blasting' },
  { id: 'HORIZ_BORING', label: 'Horizontal Boring' },
  { id: 'MANUAL_EXCAVATION', label: 'Manual Excavation' },
  { id: 'MECH_EXCAVATION', label: 'Mechanical Excavation' },
  { id: 'NON_DESTRUCTIVE_DIGGING', label: 'Non-Destructive Digging' },
  { id: 'VERTICAL_BORING', label: 'Vertical Boring' }
];

const BEL_SAME_DAY_WARNING_GROUP = 'bel-same-day-warning';

const SAME_DAY_START_WARNING_DETAILS = (
  <ol className="orderedList">
    <li>Asset owners need time to respond</li>
    <li>
      You are responsible (and liable) to obtain all necessary information
      before proceeding
    </li>
    <li>In the case of emergency, contact asset owner directly</li>
  </ol>
);

/** yyyy-mm-dd → dd/mm/yyyy for Carbon DatePickerInput (matches dateFormat d/m/Y). */
const formatIsoForPickerDisplay = (iso: string): string => {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
    return '';
  }
  const [year, month, day] = iso.split('-');
  return `${day}/${month}/${year}`;
};

/** Selected calendar date → yyyy-mm-dd in local timezone (avoid UTC shift from toISOString). */
const dateToIsoLocal = (date: Date | undefined): string => {
  if (!date) {
    return '';
  }
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

/** Mirror Lodge `setAutoRenewDate` — cap at 6 months from today, return API ISO. */
const autoRenewUntilFromJobEnd = (
  jobEnd: string | Date | undefined
): string => {
  if (!jobEnd) {
    return '';
  }
  const cappedAtSixMonths = Utils.isDateAfterExtMonth(jobEnd, 6);
  const until = cappedAtSixMonths ? Utils.getDateAfterMonths(6) : jobEnd;
  const iso = Utils.toDateAPIFormat(until);
  return iso === 'Invalid Date' ? '' : iso;
};

const isAutoRenewEligibleForJobEnd = (jobEndIso: string): boolean =>
  Boolean(
    jobEndIso && dayjs(jobEndIso).isAfter(Utils.getDateAfterDays(28), 'date')
  );

const DetailsStep: React.FC<DetailsStepProps> = ({
  details,
  validationSummary,
  onChange
}) => {
  const dispatch = useDispatch();
  const fileFields = getFileAttributeFieldsForDropdown(
    validationSummary?.fields ?? []
  );
  const hasFileAttributes = fileFields.length > 0;

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const digStartAtDate = useMemo(() => {
    if (
      !details.digStartAt ||
      !/^\d{4}-\d{2}-\d{2}$/.test(details.digStartAt)
    ) {
      return undefined;
    }
    const d = new Date(`${details.digStartAt}T00:00:00`);
    return Number.isNaN(d.getTime()) ? undefined : d;
  }, [details.digStartAt]);

  const isSameDayStart = Boolean(
    details.digStartAt && Utils.isSelectedTodayDate(details.digStartAt)
  );

  useEffect(() => {
    if (isSameDayStart) {
      dispatch(
        addAlert(
          BEL_SAME_DAY_WARNING_GROUP,
          'warning',
          'Start date is today',
          SAME_DAY_START_WARNING_DETAILS,
          { stayForever: true }
        )
      );
    } else {
      dispatch(removeAlertByGroup(BEL_SAME_DAY_WARNING_GROUP));
    }

    return () => {
      dispatch(removeAlertByGroup(BEL_SAME_DAY_WARNING_GROUP));
    };
  }, [isSameDayStart, dispatch]);

  const digEndMinDate = useMemo(() => {
    if (!digStartAtDate) return today;
    return digStartAtDate.getTime() > today.getTime() ? digStartAtDate : today;
  }, [digStartAtDate, today]);

  const updateField = (field: string, value: any) => {
    onChange({ ...details, [field]: value });
  };

  const handleDigEndAtChange = (dates: Date[]) => {
    const newEndIso = dateToIsoLocal(dates?.[0]);
    const eligible = isAutoRenewEligibleForJobEnd(newEndIso);

    onChange({
      ...details,
      digEndAt: newEndIso,
      // Lodge sets autoRenewUntil when job end changes, before the toggle is turned on
      autoRenewUntil: eligible ? autoRenewUntilFromJobEnd(newEndIso) : '',
      autoRenew: eligible ? details.autoRenew : false
    });
  };

  const hasActivityType = Boolean(
    details.activityType === 'design' ||
      details.activityType === 'planning' ||
      details.activityType === 'excavation'
  );

  const activityItems =
    details.activityType === 'design' || details.activityType === 'planning'
      ? DESIGN_ACTIVITIES
      : details.activityType === 'excavation'
      ? EXCAVATION_ACTIVITIES
      : [];

  const selectedActivityItems = useMemo(
    () =>
      activityItems.filter((item) => details.activityTypes.includes(item.id)),
    [activityItems, details.activityTypes]
  );

  const toggleLocationType = (type: string) => {
    const current = details.locationTypes;
    const updated = current.includes(type)
      ? current.filter((t) => t !== type)
      : [...current, type];
    updateField('locationTypes', updated);
  };

  return (
    <div className="bel-details-step">
      <div className="bel-details-step__card">
        <div className="bel-details-step__header">Enquiry details</div>

        <div className="bel-details-step__field-row">
          <DatePicker
            datePickerType="single"
            dateFormat="d/m/Y"
            minDate={today}
            onChange={(dates: Date[]) =>
              updateField('digStartAt', dateToIsoLocal(dates?.[0]))
            }
          >
            <DatePickerInput
              id="job-start-date"
              name="job-start-date"
              placeholder="dd/mm/yyyy"
              labelText="Job start date"
              value={formatIsoForPickerDisplay(details.digStartAt)}
              type="text"
              autoComplete="off"
            />
          </DatePicker>
          <DatePicker
            datePickerType="single"
            dateFormat="d/m/Y"
            minDate={digEndMinDate}
            onChange={handleDigEndAtChange}
          >
            <DatePickerInput
              id="job-end-date"
              name="job-end-date"
              placeholder="dd/mm/yyyy"
              labelText="Job end date"
              value={formatIsoForPickerDisplay(details.digEndAt)}
              type="text"
              autoComplete="off"
            />
          </DatePicker>
        </div>

        <div className="bel-details-step__field-row">
          <div className="bel-details-step__field-group bel-details-step__field-group--in-row">
            <span className="bel-details-step__label">Enquiry type</span>
            <RadioButtonGroup
              labelPosition="right"
              name="activity-type"
              valueSelected={
                details.activityType === 'planning'
                  ? 'design'
                  : details.activityType
              }
              onChange={(value) => {
                onChange({
                  ...details,
                  activityType: String(value),
                  activityTypes: []
                });
              }}
            >
              <RadioButton id="design" labelText="Design" value="design" />
              <RadioButton
                id="excavation"
                labelText="Excavation"
                value="excavation"
              />
            </RadioButtonGroup>
          </div>
          <div className="bel-details-step__field-group bel-details-step__field-group--in-row">
            <MultiSelect
              key={details.activityType || 'none'}
              id="bel-activities"
              titleText="Activities"
              label="Select activities"
              items={activityItems}
              disabled={!hasActivityType}
              itemToString={(item: { id: string; label: string }) =>
                item ? item.label : ''
              }
              initialSelectedItems={selectedActivityItems}
              onChange={({
                selectedItems
              }: {
                selectedItems: { id: string; label: string }[];
              }) => {
                updateField(
                  'activityTypes',
                  selectedItems.map((item) => item.id)
                );
              }}
            />
          </div>
        </div>

        <div className="bel-details-step__field-row">
          <div className="bel-details-step__field-group">
            <span className="bel-details-step__label">
              Workplace location(s)
            </span>
            <div className="bel-details-step__checkbox-group">
              {LOCATION_TYPE_OPTIONS.map((type) => (
                <Checkbox
                  key={type}
                  id={`location-${type.toLowerCase().replace(' ', '-')}`}
                  labelText={type}
                  checked={details.locationTypes.includes(type)}
                  onChange={() => toggleLocationType(type)}
                />
              ))}
            </div>
          </div>
          <div className="bel-details-step__field-group bel-details-step__field-group--in-row bel-details-step--working-on-behalf">
            <TextInput
              id="bel-working-on-behalf"
              labelText="Working on behalf"
              value="Private"
              disabled
              readOnly
              helperText="Private is set automatically for large area enquiries."
            />
          </div>
        </div>
      </div>

      <div className="bel-details-step__card">
        <div className="bel-details-step__label bel-details-step__label--bold">
          User reference
        </div>

        <div className="bel-details-step__field-group">
          <Checkbox
            id="bel-use-shape-names"
            labelText="Use attributes from my file as the user reference"
            checked={details.useShapeNamesFromFile}
            disabled={!hasFileAttributes}
            onChange={(checked: boolean) => {
              if (checked && hasFileAttributes) {
                onChange({
                  ...details,
                  useShapeNamesFromFile: true,
                  userReferenceProperty:
                    details.userReferenceProperty ||
                    pickDefaultUserReferenceProperty(fileFields)
                });
              } else {
                onChange({
                  ...details,
                  useShapeNamesFromFile: false,
                  userReferenceProperty: ''
                });
              }
            }}
          />
        </div>

        {!hasFileAttributes && (
          <div className="bel-details-step__field-group bel-details-step__user-ref-notification">
            <InlineNotification
              kind="info"
              lowContrast
              hideCloseButton
              title=""
              subtitle="Your uploaded file has no attribute columns to use as a user reference."
            />
          </div>
        )}

        {!details.useShapeNamesFromFile && hasFileAttributes && (
          <div className="bel-details-step__field-group bel-details-step__user-ref-notification">
            <InlineNotification
              kind="info"
              lowContrast
              hideCloseButton
              title=""
              subtitle="Each enquiry will be labelled using the reference names from your uploaded file. You can also enter your own reference below, or leave it blank to use the address instead."
            />
          </div>
        )}

        {details.useShapeNamesFromFile && hasFileAttributes && (
          <div className="bel-details-step__field-group">
            <Select
              id="bel-user-reference-property"
              labelText="File attribute"
              value={details.userReferenceProperty || undefined}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                updateField('userReferenceProperty', e.target.value)
              }
            >
              <SelectItem text="Choose an option" value="" disabled hidden />
              {fileFields.map((field) => (
                <SelectItem
                  key={field.name}
                  text={field.name}
                  value={field.name}
                />
              ))}
            </Select>
          </div>
        )}

        {!details.useShapeNamesFromFile && (
          <div className="bel-details-step__field-group">
            <TextInput
              id="user-reference"
              labelText="Your reference (optional)"
              placeholder="e.g. Melbourne works"
              maxLength={50}
              value={details.userReference}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                updateField('userReference', e.target.value)
              }
            />
          </div>
        )}
      </div>

      <div className="bel-details-step__card">
        <div className="bel-details-step__label">
          <span className="bel-details-step__label--bold">Notes</span>{' '}
          (optional)
        </div>
        <div className="bel-details-step__field-group bel-details-step__field-group--notes">
          <TextArea
            hideLabel
            labelText=""
            helperText="500 characters count maximum"
            id="notes"
            name="notes"
            placeholder="Share any special information about your job with asset owners"
            maxLength={500}
            value={details.notes}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              updateField('notes', e.target.value)
            }
          />
        </div>
      </div>
    </div>
  );
};

export default DetailsStep;
